import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  loadCampaignAddPage,
  unloadCampaignAddPage,
  createCampaign
} from "../../redux/actions/asset.actions";
import ListErrors from "../../components/ListErrors";
import Nav from "../../components/Nav";
import Header from "../../components/Header";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import moment from "moment";
import { Translation } from "react-i18next";
import i18n from "../../languages/lang.js";
import Select from "react-select";

const CampaignSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("campaign.form_name_invalid_error"))
    .required(i18n.t("campaign.form_name_required_error"))
});

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function() {
  const [dates, setDateRange] = useState([
    moment().toDate(),
    moment()
      .add(14, "days")
      .toDate()
  ]);
  const [assetOptions, setAssetOptions] = useState([
    { value: "all", label: "All" }
  ]);
  const [siteOptions, setSiteOptions] = useState([
    { value: "", label: i18n.t("common.option_select_site") }
  ]);
  const [site, setSite] = useState("");
  const [assets, setAssets] = useState("all");

  const dispatch = useDispatch();
  const asset = useSelector(state => state.asset);

  const query = useQuery();
  const from = query.get("from");
  const key = query.get("key");

  const formSelectStyles = {
    control: styles => ({
      ...styles,
      border: "none",
      borderRadius: 0,
      background: "#f3f3f3",
      fontSize: "1rem"
    })
  };

  useEffect(() => {
    dispatch(loadCampaignAddPage());
    return () => dispatch(unloadCampaignAddPage());
  }, [dispatch]);

  useEffect(() => {
    if (asset.sites) {
      for (let i in asset.sites) {
        siteOptions.push({
          value: asset.sites[i].key,
          label: asset.sites[i].name
        });
      }
      setSiteOptions(siteOptions);
    }
  }, [asset.sites]);

  useEffect(() => {
    if (asset.assets) {
      let options = [{ value: "all", label: "All" }];
      for (let i in asset.assets) {
        if (asset.assets[i].site === site) {
          options.push({
            value: asset.assets[i].key,
            label: asset.assets[i].name
          });
        }
      }
      setAssetOptions(options);
    }
  }, [asset.assets, site]);

  const handleSubmit = (name, notes, start_date, end_date) =>
    dispatch(createCampaign(name, notes, site, start_date, end_date, assets));

  if (!asset.sites) {
    return (
      <Translation>
        {t => (
          <div id="main">
            <Nav selected={"campaigns"} />
            <div id="content">
              <Header />
              <div id="loading">{t("system.loading")}</div>
            </div>
          </div>
        )}
      </Translation>
    );
  }
  return (
    <Translation>
      {t => (
        <div id="main">
          <Nav selected={"campaigns"} />
          <div id="content">
            <Header />
            <div className="content-wrap content-narrow">
              <div className="box-header">
                <Link className="header-button" to={`/campaigns`}>
                  {t("common.back")}
                </Link>
                <h2>{t("campaign.create_title")}</h2>
              </div>
              <Formik
                initialValues={{
                  name: "",
                  notes: ""
                }}
                validationSchema={CampaignSchema}
                onSubmit={(payload, { setSubmitting }) => {
                  handleSubmit(payload.name, payload.notes, dates[0], dates[1]);
                  setSubmitting(false);
                }}
              >
                {({ values, isSubmitting }) => (
                  <Form>
                    <div className={`form-item`}>
                      <label htmlFor="name">{t("common.site")}</label>
                      <Select
                        closeMenuOnSelect={true}
                        defaultValue={siteOptions[0]}
                        options={siteOptions}
                        onChange={e => setSite(e.value)}
                        styles={formSelectStyles}
                      />
                    </div>
                    <div className={`form-item`}>
                      <label htmlFor="name">{t("campaign.label_name")}</label>
                      <Field type="text" name="name" />
                      <ErrorMessage
                        name="name"
                        className="error-message"
                        component="div"
                      />
                    </div>
                    <div className={`form-item`}>
                      <label htmlFor="notes">{t("campaign.label_dates")}</label>
                      <DateRangePicker
                        onChange={dates => setDateRange(dates)}
                        value={dates}
                      />
                    </div>
                    <div className={`form-item`}>
                      <label htmlFor="assets">{t("common.assets")}</label>
                      <Select
                        closeMenuOnSelect={false}
                        defaultValue={[assetOptions[0]]}
                        isMulti
                        options={assetOptions}
                        onChange={e =>
                          setAssets(
                            e && e.length > 0
                              ? e.map(i => i.value).join(",")
                              : ""
                          )
                        }
                        styles={formSelectStyles}
                      />
                    </div>
                    <div className={`form-item`}>
                      <label htmlFor="notes">
                        {t("campaign.label_sms_message")}
                      </label>
                      <Field component="textarea" name="notes" />
                    </div>
                    <ListErrors errors={asset.errors} />

                    <div className="form-item">
                      <button type="submit" disabled={isSubmitting}>
                        {isSubmitting
                          ? t("system.submitting_feeback")
                          : t("campaign.create_button")}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
    </Translation>
  );
}
